/**
 * Incident Editor Dialog - Timeline Tab
 * PROJ-007 + PROJ-024 + PROJ-034 - Complete Implementation
 *
 * Features:
 * - Visual Timeline component (PROJ-034)
 * - Timestamps relativos with date-fns (PROJ-024 Fase 1)
 * - Multi-select filters by event type (PROJ-024 Fase 1)
 * - Temporal grouping (Hoy, Ayer, etc.) (PROJ-024 Fase 1)
 * - Client-side search with debounce (PROJ-024 Fase 4)
 * - Comment composition
 */

import { useState, useEffect, useMemo, useCallback, type ElementType } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertCircle,
  Clock,
  User,
  MessageSquare,
  ArrowRight,
  Send,
  Loader2,
  RefreshCw,
  Paperclip,
  TrendingUp,
  Plus,
  Edit3,
  UserCheck,
  Trash2,
  CheckCircle,
  RotateCcw,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Users,
} from 'lucide-react';
import { listTimelineEvents, addComment } from '@/lib/timelineApi';
import { TimelineFilters } from './TimelineFilters';
import { TimelineSearch } from './TimelineSearch';
import { formatRelativeTime, groupEventsByDay, getGroupOrder } from '@/utils/timeline';
import type { TimelineEvent, TimelineEventKind } from '@/types/timeline';
import { toast } from '@/hooks/use-toast';

interface TimelineTabProps {
  incidentId: string | null;
}

// Event type configuration with icons and colors
const eventTypeConfig: Record<
  TimelineEventKind,
  { icon: ElementType; iconColor: string; bgColor: string; label: string }
> = {
  created: { icon: Plus, iconColor: 'text-green-600', bgColor: 'bg-green-100', label: 'Incidente Creado' },
  updated: { icon: Edit3, iconColor: 'text-gray-600', bgColor: 'bg-gray-100', label: 'Actualizado' },
  state_changed: { icon: RefreshCw, iconColor: 'text-purple-600', bgColor: 'bg-purple-100', label: 'Estado Cambiado' },
  severity_changed: { icon: AlertTriangle, iconColor: 'text-orange-600', bgColor: 'bg-orange-100', label: 'Severidad Cambiada' },
  assigned: { icon: UserCheck, iconColor: 'text-indigo-600', bgColor: 'bg-indigo-100', label: 'Asignado' },
  reassigned: { icon: UserCheck, iconColor: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Reasignado' },
  resolved: { icon: CheckCircle, iconColor: 'text-green-600', bgColor: 'bg-green-100', label: 'Resuelto' },
  closed: { icon: CheckCircle, iconColor: 'text-gray-600', bgColor: 'bg-gray-100', label: 'Cerrado' },
  reopened: { icon: RotateCcw, iconColor: 'text-yellow-600', bgColor: 'bg-yellow-100', label: 'Reabierto' },
  cancelled: { icon: Trash2, iconColor: 'text-red-600', bgColor: 'bg-red-100', label: 'Cancelado' },
  party_added: { icon: Users, iconColor: 'text-teal-600', bgColor: 'bg-teal-100', label: 'Parte Agregada' },
  party_removed: { icon: Trash2, iconColor: 'text-red-600', bgColor: 'bg-red-100', label: 'Parte Removida' },
  evidence_added: { icon: Paperclip, iconColor: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Evidencia Agregada' },
  evidence_removed: { icon: Trash2, iconColor: 'text-red-600', bgColor: 'bg-red-100', label: 'Evidencia Removida' },
  note_added: { icon: MessageSquare, iconColor: 'text-green-600', bgColor: 'bg-green-100', label: 'Nota Agregada' },
  note_deleted: { icon: Trash2, iconColor: 'text-red-600', bgColor: 'bg-red-100', label: 'Nota Eliminada' },
  task_created: { icon: CheckCircle, iconColor: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Tarea Creada' },
  task_completed: { icon: CheckCircle, iconColor: 'text-green-600', bgColor: 'bg-green-100', label: 'Tarea Completada' },
  comment: { icon: MessageSquare, iconColor: 'text-gray-600', bgColor: 'bg-gray-100', label: 'Comentario' },
  sla_warning: { icon: AlertTriangle, iconColor: 'text-yellow-600', bgColor: 'bg-yellow-100', label: 'Advertencia SLA' },
  sla_breach: { icon: AlertTriangle, iconColor: 'text-red-600', bgColor: 'bg-red-100', label: 'Incumplimiento SLA' },
  auto_escalation: { icon: TrendingUp, iconColor: 'text-orange-600', bgColor: 'bg-orange-100', label: 'Escalación Automática' }
};

export function TimelineTab({ incidentId }: TimelineTabProps) {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // PROJ-024: Filter and search state
  const [selectedFilters, setSelectedFilters] = useState<Set<TimelineEventKind>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (incidentId) {
      loadTimeline();
    }
  }, [incidentId]);

  const loadTimeline = async () => {
    if (!incidentId) return;

    setIsLoading(true);
    try {
      const response = await listTimelineEvents(incidentId, {
        includeActor: true,
        limit: 100
      });
      setEvents(response.data || []);
    } catch (error: any) {
      console.error('Error loading timeline:', error);
      toast({
        title: 'Error',
        description: 'Error al cargar el historial de actividad',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) {
      toast({
        title: 'Validación',
        description: 'El comentario no puede estar vacío',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await addComment(incidentId!, commentText);
      toast({
        title: 'Éxito',
        description: 'Comentario agregado exitosamente'
      });
      setCommentText('');
      // Small delay to ensure cache invalidation completes
      await new Promise(resolve => setTimeout(resolve, 100));
      await loadTimeline();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Error al agregar comentario',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // PROJ-024: Filter and search callbacks
  const handleFilterChange = useCallback((filters: Set<TimelineEventKind>) => {
    setSelectedFilters(filters);
  }, []);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const toggleGroup = useCallback((label: string) => {
    setCollapsedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(label)) {
        newSet.delete(label);
      } else {
        newSet.add(label);
      }
      return newSet;
    });
  }, []);

  // PROJ-024: Filter and search logic
  const filteredEvents = useMemo(() => {
    let result = events;

    // Apply type filters
    if (selectedFilters.size > 0) {
      result = result.filter(event => selectedFilters.has(event.kind));
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(event => {
        const message = event.message?.toLowerCase() || '';
        const actorName = (event.actorUser?.fullName || event.actorPerson?.fullName || '').toLowerCase();
        const kind = event.kind.toLowerCase();
        const meta = JSON.stringify(event.meta || {}).toLowerCase();

        return message.includes(term) ||
          actorName.includes(term) ||
          kind.includes(term) ||
          meta.includes(term);
      });
    }

    return result;
  }, [events, selectedFilters, searchTerm]);

  // PROJ-024: Group events by day
  const groupedEvents = useMemo(() => {
    return groupEventsByDay(filteredEvents);
  }, [filteredEvents]);

  const groupOrder = useMemo(() => {
    return getGroupOrder(groupedEvents);
  }, [groupedEvents]);

  // Show message for new incidents
  if (!incidentId) {
    return (
      <div className="space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Guarda el incidente primero para ver el historial de actividad.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const getEventConfig = (kind: TimelineEventKind) => {
    return eventTypeConfig[kind] || {
      icon: Clock,
      iconColor: 'text-gray-600',
      bgColor: 'bg-gray-100',
      label: kind
    };
  };

  // Build description based on event type
  const getEventDescription = (event: TimelineEvent) => {
    const meta = event.meta || {};

    switch (event.kind) {
      case 'state_changed':
        return (
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline">{meta.oldState || meta.from || 'Anterior'}</Badge>
            <ArrowRight className="h-4 w-4" />
            <Badge variant="default">{meta.newState || meta.to || 'Nuevo'}</Badge>
          </div>
        );
      case 'severity_changed':
        return (
          <div className="flex items-center gap-2 flex-wrap">
            <span>Severidad:</span>
            <Badge variant="outline">{meta.oldSeverity || meta.from || '?'}</Badge>
            <ArrowRight className="h-4 w-4" />
            <Badge variant="destructive">{meta.newSeverity || meta.to || '?'}</Badge>
          </div>
        );
      case 'assigned':
      case 'reassigned':
        return (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span>Asignado a: <strong>{meta.assignedToName || meta.assignee || 'No especificado'}</strong></span>
          </div>
        );
      case 'party_added':
        return (
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span>Parte: <strong>{meta.partyName || meta.role || 'Agregada'}</strong></span>
          </div>
        );
      case 'note_added':
        return meta.subject ? (
          <span className="italic">"{meta.subject}"</span>
        ) : event.message;
      case 'evidence_added':
        return meta.fileName ? (
          <div className="flex items-center gap-2">
            <Paperclip className="h-4 w-4" />
            <span>{meta.fileName}</span>
            {meta.sizeBytes && (
              <span className="text-xs text-muted-foreground">
                ({(meta.sizeBytes / 1024).toFixed(1)} KB)
              </span>
            )}
          </div>
        ) : 'Archivo adjunto';
      default:
        return event.message;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Historial de Actividad</h3>
          <Badge variant="secondary">{events.length}</Badge>
          {filteredEvents.length !== events.length && (
            <Badge variant="outline" className="text-xs">
              Mostrando {filteredEvents.length}
            </Badge>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadTimeline}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="ml-2">Actualizar</span>
        </Button>
      </div>

      {/* PROJ-024: Search and Filters */}
      <div className="space-y-3 bg-muted/30 p-4 rounded-lg border">
        <TimelineSearch
          onSearchChange={handleSearchChange}
          placeholder="Buscar en el historial..."
        />
        <TimelineFilters onFilterChange={handleFilterChange} />
      </div>

      {/* Comment Composer */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Agregar Comentario</CardTitle>
          <CardDescription>
            Los comentarios se registran en el historial de auditoría del incidente.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="Escribe tu comentario aquí..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            rows={3}
            disabled={isSubmitting}
          />
          <Button
            onClick={handleAddComment}
            disabled={isSubmitting || !commentText.trim()}
            className="gap-2"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Agregar Comentario
          </Button>
        </CardContent>
      </Card>

      <Separator />

      {/* Timeline Events with Grouping */}
      <div className="space-y-4">
        {isLoading && (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <div className="h-10 w-10 rounded-full bg-muted" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-32 bg-muted rounded" />
                      <div className="h-3 w-full bg-muted rounded" />
                      <div className="h-3 w-20 bg-muted rounded" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!isLoading && filteredEvents.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Clock className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {events.length === 0
                ? 'No hay eventos en el historial'
                : 'No se encontraron resultados'}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              {events.length === 0
                ? 'Los cambios se registrarán automáticamente aquí.'
                : 'Intenta con otros filtros o términos de búsqueda.'}
            </p>
            {(selectedFilters.size > 0 || searchTerm) && (
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => {
                  setSelectedFilters(new Set());
                  setSearchTerm('');
                }}
              >
                Limpiar filtros
              </Button>
            )}
          </div>
        )}

        {!isLoading && filteredEvents.length > 0 && (
          <div className="space-y-6">
            {groupOrder.map(label => {
              const groupEvents = groupedEvents.get(label) || [];
              const isCollapsed = collapsedGroups.has(label);

              return (
                <Collapsible
                  key={label}
                  open={!isCollapsed}
                  onOpenChange={() => toggleGroup(label)}
                >
                  {/* Group Header */}
                  <CollapsibleTrigger asChild>
                    <button className="flex items-center gap-2 w-full text-left group hover:bg-muted/50 rounded-md px-2 py-2 transition-colors sticky top-0 z-10 bg-background/95 backdrop-blur">
                      {isCollapsed ? (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="font-semibold text-sm">{label}</span>
                      <Badge variant="secondary" className="ml-auto">
                        {groupEvents.length}
                      </Badge>
                    </button>
                  </CollapsibleTrigger>

                  {/* Group Events */}
                  <CollapsibleContent className="space-y-3 pl-6 pt-2">
                    {groupEvents.map((event, idx) => {
                      const config = getEventConfig(event.kind);
                      const IconComponent = config.icon;
                      const actorName =
                        event.actorUser?.fullName ||
                        event.actorPerson?.fullName ||
                        'Sistema';
                      const { relative, absolute } = formatRelativeTime(event.at);

                      return (
                        <Card
                          key={event.id}
                          className="shadow-sm hover:shadow-md transition-shadow"
                        >
                          <CardContent className="p-4">
                            <div className="flex gap-4">
                              {/* Icon */}
                              <div
                                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${config.bgColor}`}
                              >
                                <IconComponent className={`h-5 w-5 ${config.iconColor}`} />
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                {/* Header */}
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <span className="font-medium text-sm">
                                    {config.label}
                                  </span>
                                  <time
                                    className="text-xs text-muted-foreground whitespace-nowrap flex items-center gap-1"
                                    title={absolute}
                                  >
                                    <Clock className="h-3 w-3" />
                                    {relative}
                                  </time>
                                </div>

                                {/* Actor */}
                                <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                                  <User className="h-3 w-3" />
                                  <span>{actorName}</span>
                                </div>

                                {/* Description */}
                                <div className="text-sm text-foreground">
                                  {getEventDescription(event)}
                                </div>

                                {/* Technical details (collapsible) */}
                                {event.meta && Object.keys(event.meta).length > 0 && (
                                  <details className="text-xs mt-3">
                                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                                      Ver detalles técnicos
                                    </summary>
                                    <pre className="mt-2 rounded-md bg-muted p-2 overflow-auto text-xs">
                                      {JSON.stringify(event.meta, null, 2)}
                                    </pre>
                                  </details>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
